import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AiLoader } from "@/components/AiLoader";

/**
 * Routes that remain reachable even when a seller's access is locked
 * (expired subscription / trial). Everything else is bounced to /pricing.
 */
function isAllowedWhenLocked(pathname: string): boolean {
  return (
    pathname === "/pricing" ||
    pathname === "/onboarding" ||
    pathname === "/auth" ||
    pathname === "/reset-password" ||
    pathname === "/pending-approval" ||
    pathname === "/unsubscribe" ||
    pathname.startsWith("/legal/") ||
    pathname.startsWith("/store/") ||
    pathname.startsWith("/s/") ||
    pathname.startsWith("/shop/")
  );
}

function LockLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <AiLoader text="Loading…" />
    </div>
  );
}

/**
 * Hook returning a redirect/loader element when the access lock fires,
 * otherwise null so the caller can render the normal route tree.
 *
 * Behavior:
 *  - Direct expired seller (role=user) on a non-allowed path → redirect to /pricing
 *  - Admin impersonating an expired seller → end the session and bounce
 *    them to /dashboard?impersonation_ended=expired
 *  - Anyone else → null (no-op)
 */
export function useGlobalAccessLock(): ReactNode | null {
  const { user, role, isAccessLocked, impersonation, endImpersonation } = useAuth();
  const location = useLocation();

  if (!user || role !== "user" || !isAccessLocked) return null;
  if (isAllowedWhenLocked(location.pathname)) return null;

  if (impersonation.active) {
    void endImpersonation("subscription_expired").then(() => {
      if (typeof window !== "undefined") {
        window.location.replace("/dashboard?impersonation_ended=expired");
      }
    });
    return <LockLoader />;
  }

  return <Navigate to="/pricing" replace state={{ reason: "subscription_expired" }} />;
}

export function RoutedContent({ children }: { children: ReactNode }) {
  const lock = useGlobalAccessLock();
  return <>{lock ?? children}</>;
}

export const __testing = { isAllowedWhenLocked };
